import { useMemo } from 'react';
import { CheckCheckIcon } from 'lucide-react';
import { ProblemDetector } from './problem-detector';
import { ProblemItem } from './problem-item';
import { Problem } from './types';
import { InteractionLogEntry } from '../../../types/interaction-log-data';
import { WisdomTranscriptMessage } from '../../../../api/api-client';
import { LogType } from '../../../types/visualizer';
import { FlowLogEvent } from '../../../types/flow-log';
import { useLogNavigation } from '../../../contexts/log-navigation-context';

export const Problems = (props: {
  interactionLogs: InteractionLogEntry[];
  logType: LogType;
  wisdomMessages: WisdomTranscriptMessage[];
  flowLogs: FlowLogEvent[];
}) => {
  const { navigateToNode } = useLogNavigation();

  const problems = useMemo(() => {
    return ProblemDetector.detectProblems({
      logs: props.interactionLogs || [],
      wisdomMessages: props.wisdomMessages || [],
      flowLogs: props.flowLogs || []
    });
  }, [props.interactionLogs, props.wisdomMessages, props.flowLogs]);

  function handleFocusClick(problem: Problem) {
    if (problem.index === undefined) return;

    const logType = problem.source === 'FLOW_LOG' ? LogType.FLOW_LOG : LogType.INTERACTION_LOG;
    navigateToNode({ type: logType, index: problem.index });
  }

  if (problems.length === 0) {
    const message = 'No problems detected';

    return (
      <div className='max-h-full overflow-y-auto'>
        <div className='p-4'>
          <div className='flex items-center justify-center h-32 text-neutral-500'>
            <div className='text-center'>
              <CheckCheckIcon className='size-8 mx-auto mb-4 text-green-700' />
              <p className='text-sm'>{message}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='max-h-full overflow-y-auto'>
      <div className='p-2 px-3'>
        <div className='mb-3 flex flex-col gap-0.5'>
          <h3 className='text-sm font-medium'>Detected Issues ({problems.length})</h3>
          <p className='text-xs text-neutral-600'>
            Problems identified from interaction logs, flow logs, and transcripts
          </p>
        </div>

        {problems.map((problem) => (
          <ProblemItem key={problem.id} problem={problem} onFocusClick={handleFocusClick} />
        ))}
      </div>
    </div>
  );
};
